import { useState, useCallback, useEffect, useRef } from 'react';
import { saveAttemptProgress, loadAttempt } from '@/services/premiumProgressService';
import { supabase } from '@/integrations/supabase/client';

/**
 * Manages assessment responses in localStorage and (when an attemptId is
 * provided) syncs them to a specific premium_assessment_progress row.
 *
 * - Free assessments: pass only assessmentId, no DB sync.
 * - Premium assessments: pass attemptId so each attempt has its own
 *   localStorage namespace and its own DB row.
 */
export const useLocalStorageResponses = (assessmentId: string, attemptId?: string) => {
  // When an attemptId is provided, namespace localStorage by it so multiple
  // ongoing attempts of the same assessment do not clobber each other.
  const storageKey = attemptId
    ? `assessment_${assessmentId}_attempt_${attemptId}_responses`
    : `assessment_${assessmentId}_responses`;
  const dbSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasLoadedFromDb = useRef(false);

  const [responses, setResponsesState] = useState<Record<string, any>>(() => {
    try {
      const storedResponses = localStorage.getItem(storageKey);
      return storedResponses ? JSON.parse(storedResponses) : {};
    } catch {
      return {};
    }
  });

  // On mount, if localStorage is empty for this attempt, hydrate from the DB row.
  useEffect(() => {
    if (hasLoadedFromDb.current) return;
    if (!attemptId) return; // free assessments don't sync
    hasLoadedFromDb.current = true;

    const loadFromDb = async () => {
      try {
        const storedLocal = localStorage.getItem(storageKey);
        if (storedLocal) {
          const parsed = JSON.parse(storedLocal);
          if (Object.keys(parsed).length > 0) return;
        }

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const progress = await loadAttempt(attemptId);
        if (progress?.responses && Object.keys(progress.responses).length > 0) {
          setResponsesState(progress.responses);
          localStorage.setItem(storageKey, JSON.stringify(progress.responses));
        }
      } catch {
        // ignore
      }
    };

    loadFromDb();
  }, [assessmentId, attemptId, storageKey]);

  // Debounced sync to DB (only for premium attempts).
  const syncToDb = useCallback((updatedResponses: Record<string, any>) => {
    if (!attemptId) return;
    if (dbSyncTimerRef.current) clearTimeout(dbSyncTimerRef.current);
    dbSyncTimerRef.current = setTimeout(() => {
      saveAttemptProgress(attemptId, updatedResponses).catch(() => {});
    }, 2000);
  }, [attemptId]);

  const setResponses = useCallback((newResponses: any) => {
    try {
      const updatedResponses = typeof newResponses === 'function'
        ? newResponses(responses)
        : newResponses;

      setResponsesState(updatedResponses);

      setTimeout(() => {
        try {
          localStorage.setItem(storageKey, JSON.stringify(updatedResponses));
        } catch {
          // ignore
        }
      }, 0);

      syncToDb(updatedResponses);
    } catch {
      // ignore
    }
  }, [responses, storageKey, syncToDb]);

  const clearStoredResponses = useCallback(() => {
    setResponsesState({});
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
    if (attemptId) {
      import('@/services/premiumProgressService').then(({ deleteAttempt }) => {
        deleteAttempt(attemptId).catch(() => {});
      });
    }
  }, [storageKey, attemptId]);

  return {
    responses,
    setResponses,
    clearStoredResponses,
  };
};
